/**
 * Error Types for ERP Application
 * Provides structured error handling with detailed context
 */

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

export type ErrorCategory = 
  | 'network'
  | 'validation'
  | 'authentication'
  | 'authorization'
  | 'data'
  | 'system'
  | 'business-logic'
  | 'unknown';

export interface ErrorContext {
  /** User-friendly error message */
  message: string;
  
  /** Error severity level */
  severity: ErrorSeverity;
  
  /** Error category for classification */
  category: ErrorCategory;
  
  /** Technical error details (for support) */
  technicalDetails?: {
    errorCode?: string;
    stackTrace?: string;
    endpoint?: string;
    method?: string;
    statusCode?: number;
    timestamp: string;
    userAgent: string;
    url: string;
  };
  
  /** Suggested actions for the user */
  suggestedActions?: string[];
  
  /** Whether the error is recoverable */
  recoverable: boolean;
  
  /** Custom metadata */
  metadata?: Record<string, unknown>;
}

export interface ErrorRecoveryOptions {
  /** Show retry button */
  canRetry?: boolean;
  
  /** Retry callback */
  onRetry?: () => void | Promise<void>;
  
  /** Show reload page button */
  canReload?: boolean;
  
  /** Reload callback (if not provided, will use window.location.reload()) */
  onReload?: () => void | Promise<void>;
  
  /** Show go home button */
  canGoHome?: boolean;
  
  /** Custom recovery action */
  customAction?: {
    label: string;
    onClick: () => void | Promise<void>;
  };
}

export interface AppError extends ErrorContext {
  /** Unique error ID for tracking */
  id: string;
  
  /** Recovery options */
  recovery?: ErrorRecoveryOptions;
}

/**
 * Create a structured error from various error types
 */
export function createAppError(
  error: unknown,
  context?: Partial<ErrorContext>
): AppError {
  const timestamp = new Date().toISOString();
  const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : 'Unknown';
  const url = typeof window !== 'undefined' ? window.location.href : 'Unknown';
  
  let message = 'An unexpected error occurred';
  let category: ErrorCategory = 'unknown';
  let severity: ErrorSeverity = 'error';
  let technicalDetails: ErrorContext['technicalDetails'] = {
    timestamp,
    userAgent,
    url,
  };
  
  // Check if error has pre-attached context (for demo/test errors)
  let preAttachedContext: Partial<ErrorContext> | undefined;
  if (error && typeof error === 'object' && 'appErrorContext' in error) {
    preAttachedContext = (error as any).appErrorContext;
  }
  
  // Parse different error types
  if (error instanceof Error) {
    message = error.message;
    technicalDetails.stackTrace = error.stack;
  } else if (typeof error === 'string') {
    message = error;
  } else if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    message = (err.message as string) || message;
    
    // Handle API errors
    if ('status' in err || 'statusCode' in err) {
      category = 'network';
      technicalDetails.statusCode = (err.status || err.statusCode) as number;
      technicalDetails.endpoint = err.endpoint as string;
      technicalDetails.method = err.method as string;
    }
  }
  
  // Merge contexts: preAttachedContext takes precedence over defaults, context param takes precedence over everything
  const finalCategory = context?.category || preAttachedContext?.category || category;
  const finalSeverity = context?.severity || preAttachedContext?.severity || severity;
  const finalMessage = context?.message || preAttachedContext?.message || message;
  
  return {
    id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    message: finalMessage,
    severity: finalSeverity,
    category: finalCategory,
    technicalDetails: {
      ...technicalDetails,
      ...preAttachedContext?.technicalDetails,
      ...context?.technicalDetails,
    },
    suggestedActions: context?.suggestedActions || preAttachedContext?.suggestedActions || getDefaultSuggestedActions(finalCategory),
    recoverable: context?.recoverable ?? preAttachedContext?.recoverable ?? true,
    metadata: context?.metadata || preAttachedContext?.metadata,
    recovery: undefined,
  };
}

/**
 * Get default suggested actions based on error category
 */
function getDefaultSuggestedActions(category: ErrorCategory): string[] {
  switch (category) {
    case 'network':
      return [
        'Check your internet connection',
        'Try refreshing the page',
        'Contact support if the issue persists',
      ];
    case 'authentication':
      return [
        'Please log in again',
        'Clear your browser cache and cookies',
        'Contact your system administrator',
      ];
    case 'authorization':
      return [
        'You may not have permission to perform this action',
        'Contact your system administrator for access',
      ];
    case 'validation':
      return [
        'Please check the form for errors',
        'Ensure all required fields are filled correctly',
      ];
    case 'data':
      return [
        'The requested data may not exist',
        'Try refreshing the page',
        'Contact support if the issue persists',
      ];
    case 'system':
      return [
        'A system error has occurred',
        'Please try again in a few moments',
        'Contact support if the issue persists',
      ];
    case 'business-logic':
      return [
        'This operation cannot be completed',
        'Please review the requirements and try again',
      ];
    default:
      return [
        'Please try again',
        'Refresh the page if the issue persists',
        'Contact support for assistance',
      ];
  }
}

/**
 * Format error details for clipboard
 */
export function formatErrorForClipboard(error: AppError): string {
  const sections: string[] = [];
  
  sections.push('=== ERROR REPORT ===');
  sections.push('');
  sections.push(`Error ID: ${error.id}`);
  sections.push(`Severity: ${error.severity.toUpperCase()}`);
  sections.push(`Category: ${error.category}`);
  sections.push('');
  sections.push('USER MESSAGE:');
  sections.push(error.message);
  sections.push('');
  
  if (error.technicalDetails) {
    sections.push('TECHNICAL DETAILS:');
    sections.push(`Timestamp: ${error.technicalDetails.timestamp}`);
    sections.push(`URL: ${error.technicalDetails.url}`);
    
    if (error.technicalDetails.errorCode) {
      sections.push(`Error Code: ${error.technicalDetails.errorCode}`);
    }
    
    if (error.technicalDetails.statusCode) {
      sections.push(`HTTP Status: ${error.technicalDetails.statusCode}`);
    }
    
    if (error.technicalDetails.endpoint) {
      sections.push(`Endpoint: ${error.technicalDetails.method} ${error.technicalDetails.endpoint}`);
    }
    
    if (error.technicalDetails.stackTrace) {
      sections.push('');
      sections.push('STACK TRACE:');
      sections.push(error.technicalDetails.stackTrace);
    }
    
    sections.push('');
    sections.push(`User Agent: ${error.technicalDetails.userAgent}`);
  }
  
  if (error.metadata && Object.keys(error.metadata).length > 0) {
    sections.push('');
    sections.push('ADDITIONAL CONTEXT:');
    sections.push(JSON.stringify(error.metadata, null, 2));
  }
  
  sections.push('');
  sections.push('=== END ERROR REPORT ===');
  
  return sections.join('\n');
}
